<script lang="ts"></script>

<script setup lang="ts">
/**
 * 🅲🅾🅼🅿🅾🅽🅴🅽🆃 [Load Panel]
 * Поле загрузки фотографий
 */

import { computed, ref } from "vue";

import type { I_carouse_slide } from "@/stores/carousel/carousel.types";
import type { I_DownloadPhotos_Field_Names } from "@/stores/downloadPhotos/downloadPhotos.types";

import globalClasses from "@/css/global-classes.module.css";
import { /* isNeedConvertImageToJpeg, */ convertImageToJpeg } from "@/helpers/convertImageToJpeg";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import { useCarousel } from "@/stores/carousel/carousel";
import { useChooseDeviceStore } from "@/stores/chooseDevice/chooseDevice";
import { useImageFullScreenStore } from "@/stores/imageFullScreen/imageFullScreen";
import { useTIWStore } from "@/stores/tiw/tiw";
import Button from "@/ui/button/button.vue";
import CheckIcon from "@/ui/icons/check.vue";
import TrashIcon from "@/ui/icons/trash.vue";

import type { I_LoadPanel } from "./loadPanel.types";

import { existingVideos } from "./loadPanel";

const props = defineProps<I_LoadPanel>();

const inputRef = ref<HTMLInputElement>();
const downloadPhotoLoading = ref<boolean>(false);

const ImageFullScreenStore = useImageFullScreenStore();
const CarouselStore = useCarousel();
const { field_category } = useChooseDeviceStore();
const TIWStore = useTIWStore();

const mainColor = computed(() => {
    if (TIWStore.customStyles.color.main) {
        return TIWStore.customStyles.color.main;
    }

    return "#6959CB";
});

const hasCarouselSlides = computed(() => {
    const slides: I_carouse_slide[] = [];
    const categoryName = field_category.value.name.toLocaleLowerCase();
    const videoName = props.nameData.toLocaleLowerCase();
    const {
        INJECTED_TIW: {
            current_settings: { locale },
        },
        WIDGET_DATA: { code, location },
    } = TIWStore;

    const replacedCode = code.replace(/\./g, "_");

    const existingVideosLocale = existingVideos?.[location]?.[locale];

    const foundItems =
        existingVideosLocale?.[replacedCode]?.[categoryName]?.[videoName] ??
        existingVideosLocale?.default?.[categoryName]?.[videoName];

    foundItems?.forEach(({ type }, i) => {
        slides.push({
            url: `https://cdn.new-brz.net/mw2_prod/shorts/${location}/${locale}/${
                existingVideosLocale?.[replacedCode]?.[categoryName]?.[videoName]
                    ? replacedCode
                    : "default"
            }/${categoryName}/${videoName}/${i + 1}.${type === "image" ? "webp" : "webm"}`,
            type,
        });
    });

    return slides;
});

const isCarousel = computed(() => {
    return !props.data.isValid && hasCarouselSlides.value.length > 0 && !props.data?.message;
});

function onCarouselPreview() {
    CarouselStore.setSlides(hasCarouselSlides.value);
    CarouselStore.show();
}

function onClickImagePreview() {
    ImageFullScreenStore.setImageUrl(props.data.content);
    ImageFullScreenStore.show();
}

async function onLoadHandler(e: Event & { target: HTMLInputElement }) {
    /*
    if (isNeedConvertImageToJpeg(e.target)) {
        downloadPhotoLoading.value = true;
        await convertImageToJpeg(e.target);
        downloadPhotoLoading.value = false;
    }
    // */

    // все фотографии прогоняем через конвертер для сжатия
    downloadPhotoLoading.value = true;
    await convertImageToJpeg(e.target);
    downloadPhotoLoading.value = false;

    props.onLoad(e, props.nameData);
}

function onRemoveHandler(
    e: Event & { target: HTMLButtonElement },
    inputName: I_DownloadPhotos_Field_Names,
) {
    inputRef.value.value = "";
    props.onRemove(e, inputName);
}
</script>

<template>
    <div class="tiw__loadPanel">
        <div class="tiw__loadPanel__container">
            <figure
                class="tiw__loadPanel__figure"
                @click="isCarousel ? onCarouselPreview() : onClickImagePreview()"
            >
                <div v-if="isCarousel" class="tiw__loadPanel__tutorial">
                    <svg
                        class="tiw__loadPanel__btn-play"
                        xmlns="http://www.w3.org/2000/svg"
                        width="30"
                        height="20"
                        viewBox="0 0 30 20"
                        fill="none"
                    >
                        <rect
                            x="1"
                            y="1"
                            width="28"
                            height="18"
                            rx="4"
                            fill="url(#paint0_linear_30742_11755)"
                            stroke="#FEFEFE"
                            stroke-width="2"
                        />
                        <path
                            d="M13.5817 13.856C13.4227 13.9492 13.2419 13.9988 13.0576 14C12.8734 14.0011 12.692 13.9537 12.5318 13.8625C12.3716 13.7713 12.2381 13.6395 12.1448 13.4803C12.0514 13.3211 12.0015 13.1401 12 12.9555L12 7.04659C11.9998 6.86137 12.0488 6.67943 12.1418 6.51938C12.2349 6.35933 12.3687 6.22691 12.5296 6.13567C12.6905 6.04443 12.8727 5.99764 13.0575 6.00009C13.2424 6.00254 13.4233 6.05414 13.5817 6.14961L18.4976 9.10237C18.6511 9.19661 18.7779 9.32872 18.8659 9.48607C18.9538 9.64343 19 9.82076 19 10.0011C19 10.1814 18.9538 10.3588 18.8658 10.5161C18.7779 10.6735 18.6511 10.8056 18.4976 10.8998L13.5817 13.856Z"
                            fill="white"
                        />
                        <defs>
                            <linearGradient
                                id="paint0_linear_30742_11755"
                                x1="-6.53846"
                                y1="14.5"
                                x2="35.5"
                                y2="14.5"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop :stop-color="mainColor" />
                                <stop offset="1" :stop-color="mainColor" />
                            </linearGradient>
                        </defs>
                    </svg>
                    <img
                        class="tiw__loadPanel__img"
                        :src="props.data.content"
                        alt="Screen front imei preview"
                    />
                </div>
                <div v-else class="tiw__loadPanel__photo">
                    <img
                        class="tiw__loadPanel__img"
                        :src="props.data.content"
                        alt="Screen front imei preview"
                    />
                    <svg
                        class="tiw__loadPanel__photo__zoom-icon"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <rect width="24" height="24" rx="4" fill="white" fill-opacity="0.75" />
                        <path
                            d="M11.1667 17.5208C14.8486 17.5208 17.8333 14.5361 17.8333 10.8542C17.8333 7.17227 14.8486 4.1875 11.1667 4.1875C7.48477 4.1875 4.5 7.17227 4.5 10.8542C4.5 14.5361 7.48477 17.5208 11.1667 17.5208Z"
                            stroke="#808D9A"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M19.5 19.1875L15.875 15.5625"
                            stroke="#808D9A"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M11.1665 8.35449V13.3545"
                            stroke="#808D9A"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M8.6665 10.8545H13.6665"
                            stroke="#808D9A"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
            </figure>

            <div class="tiw__loadPanel__body">
                <!-- Подсказка [решили что не будет] -->
                <!-- <Button
                    class="tiw__loadPanel__hint-icon"
                    type="button"
                    variant="unset"
                >
                    <QuestionIcon />
                </Button> -->

                <!-- Заголовок -->
                <p class="tiw__loadPanel__title">
                    {{ props.title }}
                </p>

                <!-- Подсказка -->
                <!-- <p v-if="props.hint" class="tiw__loadPanel__hint" v-html="props.hint"></p> -->

                <!-- Alert -->
                <div
                    v-if="props.data.message"
                    class="tiw__loadPanel__note"
                    :class="[`tiw__loadPanel__note_${props.data.message.type}`]"
                >
                    <!-- <CloseIcon
                        v-if="props.data.message.type === 'error'"
                        variant="outline"
                        class="tiw__loadPanel__note__icon"
                    />
                    <AlertIcon v-else class="tiw__loadPanel__note__icon" /> -->

                    <span class="tiw__loadPanel__note__text">
                        {{ props.data.message.text }}
                    </span>
                </div>

                <template v-if="!props.data.wasUploaded">
                    <input
                        :id="props.lineId"
                        ref="inputRef"
                        type="file"
                        :name="props.name"
                        accept="image/png, image/jpeg, image/jpg, image/bmp, image/heic, .dng"
                        hidden
                        :data-name="props.nameData"
                        @change="onLoadHandler"
                    />
                </template>

                <!-- Загрузить фото -->
                <template v-if="!props.data.hideLoadButton">
                    <label
                        v-if="!props.data.placeholder"
                        class="tiw__loadPanel__loadLabel"
                        :for="props.lineId"
                        :data-name="props.nameData"
                    >
                        <Button
                            type="file"
                            color="light"
                            as="span"
                            :loading="downloadPhotoLoading"
                            :class="['tiw__loadPanel__loadButton', globalClasses.additionalButton]"
                        >
                            <span>
                                {{ useTranslate("tiw__loadPanel__downloadPhoto") }}
                            </span>
                        </Button>
                    </label>
                </template>

                <!-- Загруженное фото -->
                <div v-if="props.data.placeholder" class="tiw__loadPanel__editLabel">
                    <div
                        class="tiw__loadPanel__fileInfo"
                        :class="{
                            tiw__loadPanel__fileInfo_loading:
                                props.isLoadingStatus || props.data.wasUploaded,
                        }"
                    >
                        <CheckIcon />
                        <span>{{ props.data.placeholder }}</span>
                    </div>

                    <template v-if="!props.data.wasUploaded">
                        <template v-if="!props.isLoadingStatus">
                            <Button
                                type="button"
                                color="light"
                                class="tiw__loadPanel__removeButton"
                                @click="onRemoveHandler($event, props.nameData)"
                            >
                                <TrashIcon />
                            </Button>
                        </template>
                    </template>
                </div>
            </div>
        </div>

        <!-- Комментарий оператора -->
        <div v-if="props.data.comment" class="tiw__loadPanel__comment">
            <span>
                {{ props.data.comment.description }}
            </span>
            <ul v-if="props.data.comment.list">
                <li v-for="(item, ind) in props.data.comment.list" :key="`comment_${ind}`">
                    {{ item }}
                </li>
            </ul>
        </div>
    </div>
</template>

<style lang="scss" src="./loadPanel.scss"></style>
